import React, {useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate} from "react-router-dom";
import {CATALOG_ROUTE, FORGOT_PASSWORD_ROUTE, LOGIN_ROUTE, REGISTER_ROUTE} from "../../util/Constants";
import LoginForm from "./LoginForm";
import RegistrationForm from "./RegistrationForm";
import OAuthProviderButtons from "./OAuthProviderButtons";
import {
    fetchOAuthProviders,
    forgotPassword,
    login,
    loginGoogle,
    loginYandex,
    register,
} from "../../http/userAPI";
import {observer} from "mobx-react-lite";
import {Context} from "../../index";
import ForgotPasswordForm from "./ForgotPasswordForm";
import {mergeGuestCartOnLogin} from "../../util/mergeGuestCartOnLogin";

/** Пока в URL есть OAuth code — ждём обмен на JWT (как в SpinnerDark: cream + pine). */
function OauthSigningInOverlay() {
    return (
        <div className="flex min-h-[calc(100vh-8rem)] w-full flex-col items-center justify-center gap-4 py-8">
            <div
                className="h-10 w-10 shrink-0 animate-spin rounded-full border-2 border-cream-200 border-t-pine-700"
                role="status"
                aria-live="polite"
                aria-label="Завершение входа"
            />
            <p className="text-center text-sm text-bark-600">Завершаем вход…</p>
        </div>
    );
}

const Login = observer(() => {
    const {user, cart} = useContext(Context)
    const navigate = useNavigate()

    const location = useLocation();
    const isLogin = location.pathname === LOGIN_ROUTE

    const oauthReturnSpinner = (() => {
        const sp = new URLSearchParams(location.search);
        if (sp.get('error')) {
            return false;
        }
        if (!sp.get('code')) {
            return false;
        }
        return location.pathname === LOGIN_ROUTE || location.pathname === REGISTER_ROUTE;
    })();

    const [invalid, setInvalid] = useState('')
    const [oauthProviders, setOauthProviders] = useState({google: false, yandex: false})

    useEffect(() => {
        if (location.pathname !== LOGIN_ROUTE && location.pathname !== REGISTER_ROUTE) {
            return undefined;
        }
        let cancelled = false;
        fetchOAuthProviders()
            .then((p) => {
                if (!cancelled) {
                    setOauthProviders({google: !!p.google, yandex: !!p.yandex});
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setOauthProviders({google: false, yandex: false});
                }
            });
        return () => {
            cancelled = true;
        };
    }, [location.pathname]);

    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const oauthError = sp.get('error');
        if (oauthError) {
            const desc = sp.get('error_description');
            setInvalid(desc ? decodeURIComponent(desc.replace(/\+/g, ' ')) : oauthError);
            navigate({pathname: location.pathname}, {replace: true});
            return undefined;
        }
        const code = sp.get('code');
        if (!code) {
            return undefined;
        }
        const oauthState = sp.get('state');
        const sendCodeToBackend = async () => {
            try {
                const userData =
                    oauthState === 'yandex' ? await loginYandex(code) : await loginGoogle(code);
                user.setUser(userData);
                user.setAuth(true);
                try {
                    await mergeGuestCartOnLogin();
                    await cart.refreshTotals(true);
                } catch {
                    /* слияние корзины не блокирует вход */
                }
                window.dispatchEvent(new CustomEvent('lutica-cart-changed'));
                navigate(CATALOG_ROUTE);
            } catch (err) {
                const fallback =
                    oauthState === 'yandex'
                        ? 'Не удалось войти через Яндекс'
                        : 'Не удалось войти через Google';
                setInvalid(err.response?.data?.message || fallback);
                navigate({pathname: location.pathname}, {replace: true});
            }
        };
        sendCodeToBackend();
        return undefined;
    }, [location.search, location.pathname, navigate, user, cart])

    const [data, setData] = useState({
        email: '',
        password: '',
        passwordToCompare: ''
    })

    const restorePassword = async () => {
        try {
            const status = await forgotPassword(data.email);
            (status === 200) ? setInvalid("") : setInvalid("Ошибка, обратитесь в поддержку")
            navigate(LOGIN_ROUTE)
        } catch (err) {
            alert(err.response.data.message)
        }
    }

    const authorize = async () => {
        setInvalid('')
        try {
            let userData;
            if (!isLogin) {
                userData = await register(data.email, data.password, data.passwordToCompare)
            } else {
                userData = await login(data.email, data.password)
            }
            user.setUser(userData)
            user.setAuth(true)
            try {
                await mergeGuestCartOnLogin()
                await cart.refreshTotals(true)
            } catch {
                /* не блокируем переход в каталог */
            }
            window.dispatchEvent(new CustomEvent('lutica-cart-changed'))
            navigate(CATALOG_ROUTE)
        } catch (error) {
            setInvalid(error.response?.data?.message || 'Ошибка входа')
        }
    }

    const handleEnterPress = (e => {
        if (e.key === 'Enter') {
            authorize()
        }
    })

    if (oauthReturnSpinner) {
        return <OauthSigningInOverlay />;
    }

    return (
        <div className="flex min-h-[calc(100vh-8rem)] flex-col items-center justify-center gap-8 py-8">
            {location.pathname === LOGIN_ROUTE &&
                <LoginForm
                    key={location.pathname}
                    authorize={authorize}
                    data={data}
                    setData={setData}
                    handleEnterPress={handleEnterPress}
                    invalid={invalid}
                    oauthFooter={
                        <OAuthProviderButtons
                            google={oauthProviders.google}
                            yandex={oauthProviders.yandex}
                        />
                    }
                />
            }
            {location.pathname === REGISTER_ROUTE &&
                <RegistrationForm
                    key={location.pathname}
                    authorize={authorize}
                    data={data}
                    setData={setData}
                    handleEnterPress={handleEnterPress}
                    invalid={invalid}
                    oauthFooter={
                        <OAuthProviderButtons
                            google={oauthProviders.google}
                            yandex={oauthProviders.yandex}
                        />
                    }
                />
            }
            {location.pathname === FORGOT_PASSWORD_ROUTE &&
                <ForgotPasswordForm
                    key={location.pathname}
                    restorePassword={restorePassword}
                    data={data}
                    setData={setData}
                />
            }
        </div>
    );
});

export default Login;
